<template>
  <Teleport to="body">
    <div class="sheet-backdrop" @click.self="$emit('close')">
      <div class="sheet giant-map" role="dialog" aria-label="The whole Giant">
        <button class="sheet__close" aria-label="Close" @click="$emit('close')">✕</button>
        <p class="giant-map__eyebrow">{{ puzzle.season }} Giant</p>
        <p class="giant-map__line muted">
          <template v-if="revealing">The theme, all of it.</template>
          <template v-else>{{ percent }}% filled · {{ filled.toLocaleString() }} of {{ total.toLocaleString() }} squares. Tap anywhere to go there.</template>
        </p>
        <canvas ref="canvas" class="giant-map__canvas" @click="go" />
      </div>
    </div>
  </Teleport>
</template>

<script>
// The whole Giant at once (Matt, 2026-10-19: "the grid zooms in on your word
// and follows you, with a button to see the whole thing"): a canvas, one
// small square per cell - 10,000 DOM cells would crawl on a phone. Filled
// squares are inked, the window the grid is showing is outlined, other
// solvers are dots in their colours, and a tap jumps the cursor there.
// On the solve it is also the stage for the reveal: the theme entries light
// up across the whole grid, one by one (revealStep, as PuzzleGrid does).
import { mapState, mapGetters } from 'vuex';
import { key, letterAt, whiteCells } from '@/engine/solving';

const INK = {
  paper: '#fbf8f1', black: '#16181d', filled: '#9aa3b5', word: '#ffc94a', theme: '#f0b64b', solved: '#cfe9d1', frame: '#e05f7a',
};

export default {
  name: 'GiantMap',
  props: {
    windowAt: { type: Object, default: null }, // { r, c, span } from PuzzleGrid
    revealing: { type: Boolean, default: false },
    revealStep: { type: Number, default: 0 },
  },
  emits: ['close'],
  computed: {
    ...mapState(['puzzle', 'letters', 'cursor', 'solvedAt']),
    ...mapGetters(['currentCells', 'others']),
    total() { return whiteCells(this.puzzle).length; },
    filled() { return Object.values(this.letters).filter((v) => (typeof v === 'string' ? v : v?.l)).length; },
    percent() { return this.total ? Math.floor((this.filled / this.total) * 100) : 0; },
    themeOrder() {
      const map = {};
      [...this.puzzle.across, ...this.puzzle.down].filter((e) => e.theme).forEach((e, i) => {
        for (let j = 0; j < e.len; j += 1) map[key(e.row + (e.dir === 'down' ? j : 0), e.col + (e.dir === 'down' ? 0 : j))] = i + 1;
      });
      return map;
    },
  },
  watch: {
    letters() { this.draw(); },
    cursor: { handler() { this.draw(); }, deep: true },
    revealStep() { this.draw(); },
    others() { this.draw(); },
  },
  mounted() {
    this.onResize = () => this.draw();
    window.addEventListener('resize', this.onResize);
    this.draw();
  },
  beforeUnmount() { window.removeEventListener('resize', this.onResize); },
  methods: {
    draw() {
      const canvas = this.$refs.canvas;
      const ctx = canvas?.getContext?.('2d');
      if (!ctx) return;
      const n = this.puzzle.size;
      const width = canvas.clientWidth || 300;
      const ratio = window.devicePixelRatio || 1;
      canvas.width = Math.round(width * ratio);
      canvas.height = Math.round(width * ratio);
      const cell = (width * ratio) / n;
      const word = new Set(this.currentCells.map(([r, c]) => key(r, c)));
      ctx.fillStyle = INK.paper;
      ctx.fillRect(0, 0, canvas.width, canvas.height);
      for (let r = 0; r < n; r += 1) {
        for (let c = 0; c < n; c += 1) {
          const k = key(r, c);
          let ink = null;
          const theme = this.themeOrder[k];
          if (this.puzzle.grid[r][c] === '#') ink = INK.black;
          else if (theme && theme <= this.revealStep) ink = INK.theme;
          else if (word.has(k)) ink = INK.word;
          else if (letterAt(this.letters, r, c)) ink = this.solvedAt ? INK.solved : INK.filled;
          if (!ink) continue;
          ctx.fillStyle = ink;
          ctx.fillRect(c * cell, r * cell, Math.ceil(cell), Math.ceil(cell));
        }
      }
      for (const o of this.others) {
        if (o.row == null) continue;
        ctx.fillStyle = o.color || '#fff';
        ctx.beginPath();
        ctx.arc((o.col + 0.5) * cell, (o.row + 0.5) * cell, Math.max(cell * 1.4, 3 * ratio), 0, Math.PI * 2);
        ctx.fill();
      }
      const w = this.windowAt;
      if (w && w.span < n && !this.revealing) {
        ctx.strokeStyle = INK.frame;
        ctx.lineWidth = 2 * ratio;
        ctx.strokeRect(w.c * cell, w.r * cell, w.span * cell, w.span * cell);
      }
    },
    // The white square nearest the tap (a tap on a black square still lands).
    go(e) {
      if (this.revealing) return;
      const rect = this.$refs.canvas.getBoundingClientRect();
      const n = this.puzzle.size;
      const r0 = Math.floor(((e.clientY - rect.top) / rect.height) * n);
      const c0 = Math.floor(((e.clientX - rect.left) / rect.width) * n);
      for (let d = 0; d < n; d += 1) {
        for (let dr = -d; dr <= d; dr += 1) {
          for (let dc = -d; dc <= d; dc += 1) {
            const r = r0 + dr;
            const c = c0 + dc;
            if (Math.max(Math.abs(dr), Math.abs(dc)) !== d || r < 0 || c < 0 || r >= n || c >= n) continue;
            if (this.puzzle.grid[r][c] === '#') continue;
            this.$store.dispatch('selectCell', { row: r, col: c });
            this.$emit('close');
            return;
          }
        }
      }
    },
  },
};
</script>

<style lang="scss" scoped>
.giant-map { width: min(100%, 720px); align-items: stretch; }
.giant-map__eyebrow { margin: 0; font-size: 0.75rem; letter-spacing: 0.1em; text-transform: uppercase; color: var(--accent); font-weight: 800; }
.giant-map__line { margin: 0; font-size: 0.85rem; }
.giant-map__canvas {
  width: min(100%, 72dvh); align-self: center; aspect-ratio: 1; border-radius: 4px; border: 2px solid #111; cursor: crosshair;
  touch-action: manipulation; image-rendering: pixelated;
}
</style>
